/**
 * Клієнт серверного групування Kanban-дошки. Працює для усіх трьох таблиць
 * (persons / payments / custom) через basePath:
 *   - '/persons/kanban'
 *   - '/payments/kanban'
 *   - '/data-tables/<slug>/records/kanban'
 *
 * Кожен route будує `params` (свої фільтри + groupField + granularity) і
 * `toRecord` (мапер item → ApiRecord), формуючи KanbanSource для дошки.
 */

import { apiFetch } from './api';
import type { ApiRecord } from './data-tables-api';

export type Granularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface KanbanFacet {
  key: string;
  label: string;
  count: number;
}

export interface KanbanFacetsResponse {
  columns: KanbanFacet[];
  totalColumns: number;
  hasMore: boolean;
}

function withParams(base: URLSearchParams, extra: Record<string, string>): string {
  const p = new URLSearchParams(base);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  return p.toString();
}

export function fetchKanbanFacets(
  basePath: string,
  params: URLSearchParams,
  colPage: number,
): Promise<KanbanFacetsResponse> {
  return apiFetch(`${basePath}?${withParams(params, { mode: 'facets', colPage: String(colPage) })}`);
}

export function fetchKanbanColumnIds(
  basePath: string,
  params: URLSearchParams,
  columnKey: string,
): Promise<{ ids: string[] }> {
  return apiFetch(`${basePath}?${withParams(params, { mode: 'ids', columnKey })}`);
}

/** Сирий fetch карток колонки (items shape залежить від таблиці). */
export function fetchKanbanColumnRaw<T>(
  basePath: string,
  params: URLSearchParams,
  columnKey: string,
  page: number,
): Promise<{ items: T[]; total: number }> {
  return apiFetch(`${basePath}?${withParams(params, { mode: 'cards', columnKey, page: String(page) })}`);
}

/**
 * Джерело даних дошки — те, що route передає у <KanbanBoard>. Інкапсулює
 * таблице-специфічні URL + мапінг item → ApiRecord.
 */
export interface KanbanSource {
  /** Завантажити сторінку колонок (facets) з повними лічильниками. */
  loadFacets(colPage: number): Promise<KanbanFacetsResponse>;
  /** Завантажити сторінку карток однієї колонки. */
  loadCards(columnKey: string, page: number): Promise<{ records: ApiRecord[]; total: number }>;
  /** Усі id колонки (для select-all). */
  loadIds(columnKey: string): Promise<string[]>;
}

/** Хелпер: побудувати KanbanSource з basePath + params + мапера. */
export function makeKanbanSource<T extends { id: string }>(
  basePath: string,
  getParams: () => URLSearchParams,
  toRecord: (item: T) => ApiRecord,
): KanbanSource {
  return {
    loadFacets: (colPage) => fetchKanbanFacets(basePath, getParams(), colPage),
    loadCards: async (columnKey, page) => {
      const r = await fetchKanbanColumnRaw<T>(basePath, getParams(), columnKey, page);
      return { records: r.items.map(toRecord), total: r.total };
    },
    loadIds: async (columnKey) => (await fetchKanbanColumnIds(basePath, getParams(), columnKey)).ids,
  };
}
